import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, BadgeCheck } from "lucide-react";
import { Reveal } from "./Motion";

/**
 * Aerial Project Showcase — fully CMS-driven (Admin → Aerial Showcase).
 * Renders NOTHING publicly when no visible projects exist (no placeholder boxes).
 */
export default function AerialShowcase() {
  const [data, setData] = useState(null);
  const [lightbox, setLightbox] = useState(null); // project object
  const [playing, setPlaying] = useState(null); // project id
  const [failed, setFailed] = useState([]); // media that failed → card removed, never a broken icon

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/aerial`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setData(d))
      .catch(() => {});
  }, []);

  const closeLightbox = useCallback(() => setLightbox(null), []);
  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e) => e.key === "Escape" && closeLightbox();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, closeLightbox]);

  const markFailed = (p) =>
    setFailed((f) => (f.includes(p.id) ? f : [...f, p.id]));

  if (!data) return null;
  const projects = (Array.isArray(data.projects) ? data.projects : []).filter(
    (p) => p.visible !== false && p.src && !failed.includes(p.id)
  );
  if (projects.length === 0) return null;

  const featured = projects.find((p) => p.featured) || projects[0];
  const rest = projects.filter((p) => p.id !== featured.id);

  const caption = (p) =>
    [p.location, p.project_type].filter(Boolean).join(" · ");

  const Card = ({ p, large }) => (
    <div
      data-testid={`aerial-card-${p.id}`}
      className={`img-hover img-spotlight group relative overflow-hidden cursor-pointer bg-[#E7E2D9] ${
        large ? "aspect-[4/3] lg:aspect-auto lg:h-full" : "aspect-[4/3]"
      }`}
      onClick={() => (p.kind === "video" ? setPlaying(p.id) : setLightbox(p))}
    >
      {p.kind === "video" && playing === p.id ? (
        <video
          data-testid={`aerial-video-${p.id}`}
          poster={p.poster || undefined}
          controls
          autoPlay
          muted
          playsInline
          preload="auto"
          onError={() => setPlaying(null)}
          className="absolute inset-0 w-full h-full object-cover"
        >
          {p.src_webm && <source src={p.src_webm} type="video/webm" />}
          <source src={p.src} type="video/mp4" />
        </video>
      ) : (
        <>
          <img
            src={p.kind === "video" && p.poster ? p.poster : p.src}
            srcSet={p.kind === "video" ? undefined : p.srcset || undefined}
            sizes={large ? "(min-width:1024px) 58vw, 100vw" : "(min-width:1024px) 40vw, 100vw"}
            alt={p.alt || p.title || "Zarco's Decks project photo"}
            loading={large || p.kind === "video" ? "eager" : "lazy"}
            fetchPriority={large ? "high" : undefined}
            decoding="async"
            onError={() => markFailed(p)}
            style={{ objectPosition: p.focal || "center" }}
            className="absolute inset-0 w-full h-full object-cover"
          />
          {p.kind === "video" && (
            <span className="absolute inset-0 flex items-center justify-center">
              <span
                data-testid={`aerial-play-${p.id}`}
                className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-2xl transition-transform duration-300 group-hover:scale-110"
              >
                <Play size={26} className="text-[#111] ml-1" fill="#111" />
              </span>
            </span>
          )}
        </>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
      {(p.title || caption(p)) && (
        <div className="absolute bottom-4 left-4 md:bottom-5 md:left-5 pointer-events-none">
          {caption(p) && (
            <p className="text-[10px] md:text-[11px] uppercase tracking-[0.25em] font-semibold text-white/80">
              {caption(p)}
            </p>
          )}
          {p.title && (
            <p className="text-white text-sm md:text-base font-semibold tracking-wide mt-1">{p.title}</p>
          )}
        </div>
      )}
    </div>
  );

  return (
    <section
      data-testid="aerial-section"
      className="px-6 md:px-12 lg:px-20 py-20 md:py-28 bg-white border-t border-[#E7E2D9]"
    >
      <Reveal>
        <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">
          {data.overline}
        </p>
        <div className="grid lg:grid-cols-12 gap-8 items-end mt-4">
          <h2 className="lg:col-span-7 font-display text-4xl md:text-5xl tracking-tight font-medium text-[#111]">
            {data.heading}
          </h2>
          <p className="lg:col-span-5 text-[#4A4A4A] leading-relaxed">{data.subcopy}</p>
        </div>
        {data.credential_badge ? (
          <span
            data-testid="aerial-credential-badge"
            className="mt-6 inline-flex items-center gap-2 border border-[#99582A]/40 bg-[#FBF8F3] px-4 py-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#99582A]"
          >
            <BadgeCheck size={15} /> {data.credential_badge}
          </span>
        ) : null}
      </Reveal>

      {/* Editorial media grid — featured asset owns the visual space */}
      <div className="mt-12 grid lg:grid-cols-12 gap-4 md:gap-5">
        <Reveal className="lg:col-span-7 lg:row-span-2" y={24}>
          <Card p={featured} large />
        </Reveal>
        {rest.map((p, i) => (
          <Reveal key={p.id} delay={0.08 + i * 0.06} y={24} className="lg:col-span-5">
            <Card p={p} large={false} />
          </Reveal>
        ))}
      </div>

      {/* Lightbox */}
      {createPortal(
        <AnimatePresence>
          {lightbox && (
            <motion.div
              data-testid="aerial-lightbox"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="fixed inset-0 z-[90] bg-black/92 backdrop-blur-sm flex items-center justify-center p-4 md:p-12"
              onClick={closeLightbox}
            >
              <button
                data-testid="aerial-lightbox-close"
                aria-label="Close"
                className="absolute top-5 right-5 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              >
                <X size={22} />
              </button>
              <motion.figure
                initial={{ scale: 0.96, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.97, opacity: 0 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="max-w-5xl w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={lightbox.src}
                  srcSet={lightbox.srcset || undefined}
                  sizes="90vw"
                  alt={lightbox.alt || lightbox.title || "Zarco's Decks project photo"}
                  onError={() => { markFailed(lightbox); closeLightbox(); }}
                  className="w-full max-h-[80vh] object-contain"
                />
                {(lightbox.title || caption(lightbox)) && (
                  <figcaption className="mt-4 text-center">
                    {caption(lightbox) && (
                      <p className="text-[11px] uppercase tracking-[0.25em] font-semibold text-white/60">
                        {caption(lightbox)}
                      </p>
                    )}
                    {lightbox.title && (
                      <p className="text-white font-semibold tracking-wide mt-1">{lightbox.title}</p>
                    )}
                  </figcaption>
                )}
              </motion.figure>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </section>
  );
}
